// src/theme/contrast.ts
function hexToRgb(hex) {
  let h = hex.trim();
  if (h.startsWith("#")) h = h.slice(1);
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("");
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) {
    throw new Error(`hexToRgb: invalid hex color "${hex}"`);
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
}
function rgbToHex({ r, g, b }) {
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
  const hex = (n) => clamp(n).toString(16).padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}
function hexToHsl(hex) {
  const { r, g, b } = hexToRgb(hex);
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const delta = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === rN) {
      h = (gN - bN) / delta + (gN < bN ? 6 : 0);
    } else if (max === gN) {
      h = (bN - rN) / delta + 2;
    } else {
      h = (rN - gN) / delta + 4;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}
function hslToHex({ h, s, l }) {
  const hN = (h % 360 + 360) % 360 / 360;
  const sN = Math.max(0, Math.min(100, s)) / 100;
  const lN = Math.max(0, Math.min(100, l)) / 100;
  if (sN === 0) {
    const gray = lN * 255;
    return rgbToHex({ r: gray, g: gray, b: gray });
  }
  const q = lN < 0.5 ? lN * (1 + sN) : lN + sN - lN * sN;
  const p = 2 * lN - q;
  const hue2rgb = (t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const r = hue2rgb(hN + 1 / 3) * 255;
  const g = hue2rgb(hN) * 255;
  const b = hue2rgb(hN - 1 / 3) * 255;
  return rgbToHex({ r, g, b });
}
function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
function contrastRatio(hex1, hex2) {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}
var DARK_PRIMARY_FALLBACK = "#C97AA0";
function deriveDarkPrimary(primaryHex, darkBgHex = "#1A202C", targetRatio = 4.5) {
  if (contrastRatio(primaryHex, darkBgHex) >= targetRatio) {
    return primaryHex;
  }
  const hsl = hexToHsl(primaryHex);
  for (let l = Math.ceil(hsl.l) + 1; l <= 100; l++) {
    const candidate = hslToHex({ h: hsl.h, s: hsl.s, l });
    if (contrastRatio(candidate, darkBgHex) >= targetRatio) {
      return candidate;
    }
  }
  if (typeof console !== "undefined" && console.warn) {
    console.warn(
      `[deriveDarkPrimary] Could not derive a dark-safe shade of "${primaryHex}" against "${darkBgHex}" at target ${targetRatio}:1. Falling back to ${DARK_PRIMARY_FALLBACK}.`
    );
  }
  return DARK_PRIMARY_FALLBACK;
}

// src/theme/index.ts
var DEFAULT_PRIMARY = "#9A3F69";
var DEFAULT_SECONDARY = "#2D3748";
var DEFAULT_FONT_FAMILY = "system-ui, -apple-system, sans-serif";
var DEFAULT_THEME = {
  primaryColor: DEFAULT_PRIMARY,
  // Pre-computed for the default plum — round-trips identically through
  // `deriveDarkPrimary(DEFAULT_PRIMARY)`. Kept inline so the default theme
  // object is a plain literal (no module-init side effects on import).
  darkPrimary: deriveDarkPrimary(DEFAULT_PRIMARY),
  secondaryColor: DEFAULT_SECONDARY,
  fontFamily: DEFAULT_FONT_FAMILY,
  fontSize: {
    small: 12,
    body: 18,
    heading: 24,
    dateTime: 32,
    button: 18
  },
  touchTargets: {
    minimum: 48,
    emergency: 80
  }
};
function createTheme(branding) {
  const primary = branding?.primaryColor || DEFAULT_PRIMARY;
  const secondary = branding?.secondaryColor || DEFAULT_SECONDARY;
  const fontFamily = branding?.fontFamily || DEFAULT_FONT_FAMILY;
  return {
    ...DEFAULT_THEME,
    primaryColor: primary,
    darkPrimary: deriveDarkPrimary(primary),
    secondaryColor: secondary,
    fontFamily,
    logo: branding?.logo
  };
}
var GLASS = {
  /** Lichte modus: melkwit, zodat donkere tekst leesbaar blijft. */
  background: "rgba(255, 255, 255, 0.72)",
  backgroundDark: "rgba(22, 22, 26, 0.62)",
  /** De randlijn IS het glas: zonder die lichte kier zweeft een vlak in plaats van te liggen. */
  border: "rgba(255, 255, 255, 0.55)",
  borderDark: "rgba(255, 255, 255, 0.10)",
  shadow: "0 8px 32px rgba(16, 16, 18, 0.12)",
  shadowDark: "0 8px 32px rgba(0, 0, 0, 0.45)",
  filter: "saturate(180%) blur(14px)",
  // ── EEN TWEEDE, LICHTERE VARIANT VOOR BEDIENING OVER BEELD ───────────────
  //
  // De ronde pijlknoppen op de presentatiepagina's liggen op een donkere afbeelding, niet
  // op een pagina. Daar hoort glas dat het beeld nauwelijks aantast: je moet er doorheen
  // kunnen kijken, want wat eronder ligt is de inhoud. Ze in `glassSurface()` persen zou
  // ze in melkglas veranderen en precies verbergen waar ze overheen liggen.
  controlBackground: "rgba(255, 255, 255, 0.14)",
  controlBackgroundHover: "rgba(255, 255, 255, 0.26)",
  controlBorder: "rgba(255, 255, 255, 0.22)",
  // ── EN EEN DICHTERE VARIANT VOOR INHOUD ──────────────────────────────────
  //
  // Een zwevende balk mag doorzichtig zijn; een dialoog met een formulier of een tabel
  // niet. Dichte tekst op te veel doorschijn is moeilijk te lezen, en dat weegt zwaarder
  // dan het effect. Vandaar één trap dichter voor oppervlakken die inhoud dragen.
  backgroundStrong: "rgba(255, 255, 255, 0.88)",
  backgroundStrongDark: "rgba(26, 26, 31, 0.86)"
};
function glassSurface(opts = {}) {
  return {
    backgroundColor: opts.strong ? "var(--caro-glass-bg-strong)" : "var(--caro-glass-bg)",
    backdropFilter: "var(--caro-glass-filter)",
    WebkitBackdropFilter: "var(--caro-glass-filter)",
    border: "1px solid var(--caro-glass-border)",
    boxShadow: "var(--caro-glass-shadow)"
  };
}
function glassControl() {
  return {
    backgroundColor: "var(--caro-glass-control-bg)",
    backdropFilter: "var(--caro-glass-filter)",
    WebkitBackdropFilter: "var(--caro-glass-filter)",
    border: "1px solid var(--caro-glass-control-border)",
    transition: "background-color 160ms ease-out",
    "&:hover": { backgroundColor: "var(--caro-glass-control-bg-hover)" }
  };
}
function themeToCssVars(theme) {
  const vars = {
    "--caro-primary": theme.primaryColor,
    "--caro-primary-dark": theme.darkPrimary,
    "--caro-secondary": theme.secondaryColor,
    "--caro-font": theme.fontFamily,
    "--font-small": `${theme.fontSize.small}px`,
    "--font-body": `${theme.fontSize.body}px`,
    "--font-heading": `${theme.fontSize.heading}px`,
    "--font-datetime": `${theme.fontSize.dateTime}px`,
    "--font-button": `${theme.fontSize.button}px`,
    "--touch-min": `${theme.touchTargets.minimum}px`,
    "--touch-emergency": `${theme.touchTargets.emergency}px`,
    // Glas. De `-dark`-varianten rijden mee zodat een donkere modus de basisvariabele
    // erheen kan wijzen — hetzelfde patroon als `--caro-primary-dark` hierboven, en om
    // dezelfde reden: één omschakeling in CSS in plaats van een tweede tokenset in JS.
    "--caro-glass-bg": GLASS.background,
    "--caro-glass-bg-dark": GLASS.backgroundDark,
    "--caro-glass-border": GLASS.border,
    "--caro-glass-border-dark": GLASS.borderDark,
    "--caro-glass-shadow": GLASS.shadow,
    "--caro-glass-shadow-dark": GLASS.shadowDark,
    "--caro-glass-filter": GLASS.filter,
    "--caro-glass-control-bg": GLASS.controlBackground,
    "--caro-glass-control-bg-hover": GLASS.controlBackgroundHover,
    "--caro-glass-control-border": GLASS.controlBorder,
    "--caro-glass-bg-strong": GLASS.backgroundStrong,
    "--caro-glass-bg-strong-dark": GLASS.backgroundStrongDark
  };
  if (theme.logo) {
    vars["--caro-logo-url"] = `url(${theme.logo})`;
  }
  return vars;
}
function applyThemeCssVars(theme, target) {
  if (typeof document === "undefined" && !target) return {};
  const el = target ?? document.documentElement;
  const vars = themeToCssVars(theme);
  for (const [name, value] of Object.entries(vars)) {
    el.style.setProperty(name, value);
  }
  return vars;
}

export { DARK_PRIMARY_FALLBACK, DEFAULT_THEME, GLASS, applyThemeCssVars, contrastRatio, createTheme, deriveDarkPrimary, glassControl, glassSurface, hexToHsl, hslToHex, relativeLuminance, themeToCssVars };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map